import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Field, FieldContent, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Switch } from "@/components/ui/switch";
import { InputField, ComboboxInputField, FormattedNumberInputField } from "@/components/shared";
import { provincesSimplified } from "@/constants/provinces";
import { ADVANCED_COST_PARAMETERS_CONFIG } from "@/constants/advancedCostParameters";
import { TCreateOrganizationFormSchema } from "../CreateOrganiationsModel";

interface Props {
  form: UseFormReturn<TCreateOrganizationFormSchema>;
  parentOrganizationOptions: Array<{ label: string; value: number }>;
  allOrganizationsLoading: boolean;
}

/**
 * Base form component for creating organizations.
 * Contains the main organization fields and advanced cost parameters.
 *
 * @component
 * @example
 * <BaseCreateFormComponent
 *   form={form}
 *   parentOrganizationOptions={parentOptions}
 *   allOrganizationsLoading={false}
 * />
 */
function BaseCreateFormComponent({ form, parentOrganizationOptions, allOrganizationsLoading }: Props) {
  const { t } = useTranslation(["modals", "translation"]);

  const isMainOrganization = form.watch("isMainOrganization");

  return (
    <div className="flex-1 flex flex-col gap-4 min-w-[400px]">
      <ScrollArea className="flex-1 min-h-0">
        <div className="flex flex-col gap-4">
          <InputField
            label={t("createorganization.inputs.name.label")}
            name="input.organization.name"
            placeholder={t("createorganization.inputs.name.placeholder")}
            form={form}
            className="w-full"
            disabled={false}
          />

          <div className="grid grid-cols-2 gap-4">
            <InputField
              label={t("createorganization.inputs.shortname.label")}
              name="input.organization.shortName"
              placeholder={t("createorganization.inputs.shortname.placeholder")}
              form={form}
              className="w-full"
              disabled={false}
            />

            <ComboboxInputField
              form={form}
              name="input.organization.province"
              label={t("createorganization.inputs.province.label")}
              options={provincesSimplified.map((province) => ({
                label: province.name,
                value: province.code
              }))}
              className="w-full"
            />
          </div>

          <Field orientation="horizontal" className="border rounded-lg p-4">
            <FieldContent>
              <FieldLabel htmlFor="isMainOrganization">
                {t("createorganization.inputs.ismainorganization.label")}
              </FieldLabel>
              <FieldDescription>
                {isMainOrganization
                  ? t("createorganization.inputs.ismainorganization.description.main")
                  : t("createorganization.inputs.ismainorganization.description.sub")
                }
              </FieldDescription>
              {!isMainOrganization && (
                <div className="mt-3">
                  <ComboboxInputField
                    form={form}
                    name="input.parentId"
                    label={t("createorganization.inputs.parent.label")}
                    placeholder={t("createorganization.inputs.parent.placeholder")}
                    options={parentOrganizationOptions}
                    disabled={allOrganizationsLoading}
                    loading={allOrganizationsLoading}
                    className="w-full"
                  />
                </div>
              )}
            </FieldContent>
            <Switch
              id="isMainOrganization"
              checked={isMainOrganization}
              onCheckedChange={(checked) => form.setValue("isMainOrganization", checked)}
            />
          </Field>

          {isMainOrganization && (
            <div className="border rounded-lg">
              <h3 className="text-sm px-4 pt-2 text-muted-foreground">{t("createorganization.advancedcosts.title")}</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
                {ADVANCED_COST_PARAMETERS_CONFIG.map((config) => (
                  <FormattedNumberInputField
                    key={config.name}
                    control={form.control}
                    name={`advancedCostParameters.${config.name}` as keyof TCreateOrganizationFormSchema}
                    label={t(config.labelKey)}
                    placeholder={t(config.placeholderKey)}
                    isInteger={config.isInteger}
                    isStandalone
                    style={config.style}
                    className="w-full"
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

export default BaseCreateFormComponent;
